import * as React from 'react'
import TopNavigation from '../components/Navigation/TopNavigation'
import { AppState } from '../store'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import ERoute from '../ERoute'
import { RouteComponentProps } from 'react-router'
import ScrollToTopOnMount from '../components/Effects/ScrollToTopOnMount'
import { showConfirmModal, showNewsCenterModal } from '../store/modals/actions'
import PageContent from '../components/Page/PageContent'
import PageHeader from '../components/Page/PageHeader'
import { Helmet } from 'react-helmet'
import { withTranslation, WithTranslation } from 'react-i18next'
import { NewsCenter, NewsCenterFilter, NewsCenterFilters, CurrentUser, ResourceListFilterType } from '../types'
import LocalStorage, { LocalStorageKey } from '../LocalStorage'
import ResourceTableRow from '../components/Resource/ResourceTableRow'
import ResourceTableRowData from '../components/Resource/ResourceTableRowData'
import ResourceTableRowActions from '../components/Resource/ResourceTableRowActions'
import CardEmptyInfo from '../components/Card/CardEmptyInfo'
import ResourceTable, { ResourceTableAction } from '../components/Resource/ResourceTable'
import PageLoader from '../components/Page/PageLoader'
import { NewsCentersController } from '../controllers'
import RouteHelper from '../helpers/RouteHelper'
import Notification from '../utilities/Notification'
import Icon from '../components/Icons/Icon'
import SearchHelper from '../helpers/SearchHelper'
import NavigationDetection from '../components/Effects/NavigationDetection'
import Switch from '../components/Form/Switch'

interface IStateProps extends RouteComponentProps<{ groupId?: string }> {
	currentUser: CurrentUser
}

interface IDispatchToProps {
	showNewsCenterModal: typeof showNewsCenterModal
	showConfirmModal: typeof showConfirmModal
}

interface IState {
	newsCenters: NewsCenter[],
	page: number,
	perPage: number
	totalPages: number
	didInitialLoad: boolean
	isFetching: boolean
	sortValue: string
	activeFilters: any,
	selectedNewsCenterIds: string[]
	search: string
}

type IProps = IStateProps & IDispatchToProps & WithTranslation

class NewsCenters extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props)

		const { search, perPage, page, filters } = SearchHelper.getSearch(this.props.location.search, NewsCenterFilters)

		this.state = {
			newsCenters: [],
			page: page,
			perPage: perPage,
			totalPages: 0,
			didInitialLoad: false,
			isFetching: false,
			sortValue: LocalStorage.get(LocalStorageKey.NEWS_CENTER_SORT_VALUE, '-'),
			selectedNewsCenterIds: [],
			search: search,
			activeFilters: filters,
		}

		this.onRouteChange = this.onRouteChange.bind(this)
		this.onNewNewsCenterClick = this.onNewNewsCenterClick.bind(this);
		this.onTableNewsCenterClick = this.onTableNewsCenterClick.bind(this)
		this.onTableActionClick = this.onTableActionClick.bind(this)
		this.onTableSelectionChange = this.onTableSelectionChange.bind(this)
		this.onTableRowSelectionChange = this.onTableRowSelectionChange.bind(this)
		this.onTableNewsCenterDeleteClick = this.onTableNewsCenterDeleteClick.bind(this);
		this.onNewsCenterFormSubmit = this.onNewsCenterFormSubmit.bind(this)
		this.onNewsCenterUpdateFormSubmit = this.onNewsCenterUpdateFormSubmit.bind(this)
		this.onNewsCenterCreate = this.onNewsCenterCreate.bind(this)
		this.onNewsCenterFiltersChange = this.onNewsCenterFiltersChange.bind(this)
		this.onNewsCenterSortValueChange = this.onNewsCenterSortValueChange.bind(this)
		this.onNewsCenterSearchChange = this.onNewsCenterSearchChange.bind(this)
		this.onNewsCenterSearchSubmit = this.onNewsCenterSearchSubmit.bind(this)
		this.onNewsCenterClearFilters = this.onNewsCenterClearFilters.bind(this)
		this.onBulkDeleteClick = this.onBulkDeleteClick.bind(this)
		this.onPageChange = this.onPageChange.bind(this)
		this.onPerPageChange = this.onPerPageChange.bind(this)
		this.onToggleEnabledChange = this.onToggleEnabledChange.bind(this)
	}

	onRouteChange() {
		const { search, page, perPage, filters } = SearchHelper.getSearch(this.props.location.search, NewsCenterFilters)

		this.setState({
			search: search,
			page: page,
			perPage: perPage,
			activeFilters: filters
		}, () => {
			this.fetchNewsCenters(page)
		})
	}

	async fetchNewsCenters(page: number) {
		const { search, sortValue, activeFilters, perPage } = this.state
		this.setState({ isFetching: true }, async () => {
			try {
				const response = await NewsCentersController.getNewsCenters({
					page: page,
					per_page: perPage,
					search: search,
					order: `${sortValue}`,
					...activeFilters
				})
				const { news_centers, current_page, total_pages, total_entries } = response;

				this.setState({
					newsCenters: [...news_centers],
					page: current_page,
					totalPages: total_pages,
					didInitialLoad: true,
					isFetching: false
				});
			} catch (ex) {
				console.error(ex)
			}
		});
	}

	onNewNewsCenterClick(e: React.MouseEvent<HTMLAnchorElement>) {
		e.preventDefault()

		const { showNewsCenterModal } = this.props

		showNewsCenterModal({
			newsCenter: {

			},
			onSubmit: this.onNewsCenterFormSubmit,
		})
	}

	onTableNewsCenterClick(newsCenter: NewsCenter) {
		this.props.history.push(RouteHelper.process(ERoute.PATH_NEWS_CENTER, { id: newsCenter.id }))
	}

	onTableActionClick(key: string, newsCenter: NewsCenter) {
		switch (key) {
			case 'delete': this.onTableNewsCenterDeleteClick(newsCenter)
				break
			default:
				throw Error('[NewsCenters] Unimplemented onTableActionClick')
		}
	}

	onTableSelectionChange(selectedNewsCenterIds: string[]) {
		this.setState({ selectedNewsCenterIds: selectedNewsCenterIds })
	}

	onTableRowSelectionChange(selected: boolean, transactionId: string) {
		const { selectedNewsCenterIds } = this.state

		if (selected) {
			this.setState({ selectedNewsCenterIds: [...selectedNewsCenterIds, transactionId] })
		} else {
			this.setState({ selectedNewsCenterIds: selectedNewsCenterIds.filter(selectedTransactionId => selectedTransactionId !== transactionId) })
		}
	}

	onTableNewsCenterDeleteClick(newsCenter: NewsCenter) {
		const { showConfirmModal, t } = this.props

		showConfirmModal({
			title: t('NewsCenter::Delete news center'),
			description: t('NewsCenter::You are about to delete {{name}}. By deleting this news center you are also deleting all its associated data. Are you sure?', { name: newsCenter.name }),
			action: { label: t('NewsCenter::Delete'), isDestructive: true },
			onConfirm: () => {
				NewsCentersController
					.delete(newsCenter.id)
					.then((response) => {
						if (response.errors) {

						} else {
							const { newsCenters } = this.state;

							const newsCenterIndex = newsCenters.findIndex(c => c.id === newsCenter.id);

							newsCenters.splice(newsCenterIndex, 1);

							this.setState({
								newsCenters: newsCenters
							});

							Notification.notifySuccess(t('NewsCenter::News center successfully deleted'))
						}
					})
					.catch(console.error)
			}
		})
	}

	onNewsCenterFormSubmit(newsCenter: NewsCenter) {
		const { newsCenters } = this.state;

		const newNewsCenters = [newsCenter, ...newsCenters];

		this.setState({ newsCenters: newNewsCenters });
	}

	onNewsCenterUpdateFormSubmit(newsCenter: NewsCenter) {
		const { newsCenters } = this.state

		const newsCenterIndex = newsCenters.findIndex(c => c.id === newsCenter.id);

		if (newsCenterIndex !== -1) {
			newsCenters[newsCenterIndex] = newsCenter
		}

		this.setState({
			newsCenters: [
				...newsCenters,
			]
		})
	}

	onNewsCenterCreate(data) {
		const { newsCenters } = this.state
		const { detail: { newsCenter } } = data

		this.setState({
			newsCenters: [
				newsCenter,
				...newsCenters,
			]
		})
	}

	onNewsCenterFiltersChange(activeFilters: any) {
		this.setState({ page: 1, activeFilters: activeFilters }, () => {
			const { page, activeFilters, search, sortValue } = this.state
			RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
		})
	}

	onNewsCenterSortValueChange(value: string) {
		LocalStorage.set(LocalStorageKey.NEWS_CENTER_SORT_VALUE, value)
		this.setState({ page: 1, sortValue: value }, () => {
			const { page, activeFilters, search, sortValue } = this.state
			RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
		})
	}

	onNewsCenterSearchChange(search) {
		this.setState({ search: search })
	}

	onNewsCenterSearchSubmit(search) {
		this.setState({ page: 1, search: search }, () => {
			const { page, activeFilters, search, sortValue } = this.state
			RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
		})
	}

	onNewsCenterClearFilters() {
		this.setState({
			page: 1,
			search: '',
			activeFilters: {}
		}, () => {
			const { page, activeFilters, search, sortValue } = this.state
			RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
		})
	}

	onBulkDeleteClick() {
		const { showConfirmModal, t } = this.props
		const { newsCenters, selectedNewsCenterIds } = this.state

		showConfirmModal({
			title: t('NewsCenter::Delete news centers'),
			description: t('NewsCenter::You are about to delete {{count}} news centers. By deleting these news centers you are also deleting all their associated data. Are you sure?', { count: selectedNewsCenterIds.length }),
			action: { label: t('NewsCenter::Delete'), isDestructive: true },
			onConfirm: async () => {
				try {
					await NewsCentersController.bulkDelete(selectedNewsCenterIds)

					this.setState({
						selectedNewsCenterIds: [],
						newsCenters: newsCenters.filter(newsCenter => !selectedNewsCenterIds.includes(newsCenter.id))
					})
				} catch (ex) {
					console.error(ex)
				}
			}
		})
	}

	onPageChange(page: number) {
		this.setState({ page: page }, () => {
			const { page, activeFilters, search, sortValue } = this.state
			RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
		})
	}

	onPerPageChange(perPage: number) {
		this.setState({ page: 1, perPage: perPage }, () => {
			const { page, perPage, activeFilters, search, sortValue } = this.state
			LocalStorage.set(LocalStorageKey.PER_PAGE, perPage)
			RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, perPage: perPage, search: search, ...activeFilters, sortValue: sortValue })
		})
	}

	async onToggleEnabledChange(e: any, newsCenter: NewsCenter) {
		e.preventDefault()
		e.stopPropagation()
		const { newsCenters } = this.state

		try {
			const enabled = !newsCenter.enabled
			this.setState({ newsCenters: newsCenters.map(c => c.id === newsCenter.id ? { ...c, enabled: enabled } : c) })
			await NewsCentersController.update({ ...newsCenter, enabled: enabled })
		} catch (ex) {
			console.error(ex)
		}
	}

	render() {
		const { currentUser, t } = this.props
		const { newsCenters, didInitialLoad, isFetching, activeFilters, sortValue, search, page, perPage, totalPages, selectedNewsCenterIds } = this.state
		const { workspace: { setting } } = currentUser

		const activeFiltersCount = search?.length > 0 || Object.keys(activeFilters).length > 0
		const promotedBulkActions: ResourceTableAction[] = [
			{ icon: 'trash-alt-solid', content: t('NewsCenters::Delete'), onAction: this.onBulkDeleteClick, destructive: true }
		]

		const bulkActions: ResourceTableAction[] = []
		const filters: NewsCenterFilter[] = [
			{ name: 'name', label: t('NewsCenter::Name'), type: ResourceListFilterType.STRING },
			{ name: 'enabled', label: t('NewsCenter::Enabled'), type: ResourceListFilterType.SINGLE_OPTION, options: [{ label: t('NewsCenters::Yes'), value: String(true) }, { label: t('NewsCenters::No'), value: String(false) }] },
			{ name: 'created_at', label: t('NewsCenter::Created date'), type: ResourceListFilterType.DATE },
		]

		return (
			<>
				<Helmet>
					<title>{t('NewsCenters::{{__appName}} | News centers')}</title>
				</Helmet>
				<ScrollToTopOnMount />
				<NavigationDetection onRouteChange={this.onRouteChange} />
				<TopNavigation
					icon='book'
					title={t('NewsCenters::News centers')}
					action={
						<a key='new-project' href='javascript://' className='button button-primary page-action' onClick={this.onNewNewsCenterClick}>
							<Icon icon='plus' />
							{t('NewsCenters::Add news center')}
						</a>
					}
				/>

				<PageContent>
					<PageHeader
						title={t('NewsCenters::News Centers')}
					/>

					{!didInitialLoad && <PageLoader />}
					{didInitialLoad && <ResourceTable
						data={newsCenters}
						headers={[
							{ title: t('NewsCenters::Name') },
							{ title: t('NewsCenters::Enabled') },
							{ title: '', stickyRight: '0px' },
						]}
						renderRow={(newsCenter: NewsCenter) => {
							return (
								<ResourceTableRow
									key={newsCenter.id}
									selected={selectedNewsCenterIds.includes(newsCenter.id)}
									onSelectionChange={(selected) => this.onTableRowSelectionChange(selected, newsCenter.id)}
								>
									<ResourceTableRowData onClick={() => this.onTableNewsCenterClick(newsCenter)} maxWidth='500px' ellipse>
										{newsCenter.name}
									</ResourceTableRowData>
									<ResourceTableRowData onClick={() => this.onTableNewsCenterClick(newsCenter)} ellipse>
										<Switch
											name='enabled'
											checked={newsCenter.enabled}
											onClick={(e) => this.onToggleEnabledChange(e, newsCenter)}
										/>
									</ResourceTableRowData>
									<ResourceTableRowActions
										actions={[
											{ key: 'delete', icon: 'trash-alt-solid', content: t('NewsCenter::Delete'), destructive: true }
										]}
										onActionClick={(key) => this.onTableActionClick(key, newsCenter)}
										sticky={true}
										stickyRight='0px'
									/>
								</ResourceTableRow>
							)
						}}
						renderEmpty={<CardEmptyInfo
							icon={activeFiltersCount ? 'search' : 'bullhorn'}
							description={activeFiltersCount ? t('NewsCenter::No news centers found') : t('NewsCenter::No news centers have been created yet')}
							descriptionActionText={activeFiltersCount ? t('NewsCenter::Clear filters') : t('NewsCenter::Add news center')}
							onDescriptionActionClick={activeFiltersCount ? this.onNewsCenterClearFilters : this.onNewNewsCenterClick}
						/>}
						filters={filters}
						activeFilters={activeFilters}
						onFiltersChange={this.onNewsCenterFiltersChange}
						sortOptions={[
							{ label: '-', value: '-' },
							{ label: t('NewsCenter::Name (A-Z)'), value: 'name_asc' },
							{ label: t('NewsCenter::Name (Z-A)'), value: 'name_desc' },
							{ label: t('NewsCenter::Created at ↑'), value: 'created_at_asc' },
							{ label: t('NewsCenter::Created at ↓'), value: 'created_at_desc' },
						]}
						promotedBulkActions={promotedBulkActions}
						bulkActions={bulkActions}
						sortValue={sortValue}
						onSortChange={this.onNewsCenterSortValueChange}
						pagination={{ page: page, perPage: perPage, pageCount: totalPages }}
						onPageChange={this.onPageChange}
						isLoading={isFetching}
						stickyHeader={true}
						selectedItems={selectedNewsCenterIds}
						onSelectionChange={this.onTableSelectionChange}
						searchValue={search}
						onSearchChange={this.onNewsCenterSearchChange}
						onSearchSubmit={this.onNewsCenterSearchSubmit}
						maxHeight='60vh'
					/>}
				</PageContent>
			</>
		)
	}
}

interface IStateToProps {
	currentUser: CurrentUser
}

const mapStateToProps = (state: AppState): IStateToProps => {
	const {
		authentication: {
			currentUser,
		}
	} = state

	return {
		currentUser: currentUser,
	}
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
	return {
		showNewsCenterModal: (options) => dispatch(showNewsCenterModal(options)),
		showConfirmModal: (options) => dispatch(showConfirmModal(options)),
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(NewsCenters))